// Product policy for the Clarity consent regions, not a worldwide legal registry.
// Keep this independent from language, timezone and cached SSR HTML.
export const ANALYTICS_CONSENT_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
  // EU outermost regions have their own country codes in IP databases.
  'AX', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF',
]);
const countries = new Set(('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' '));

export function countryFromTrace(text) {
  if (typeof text !== 'string' || text.length > 4096) return null;
  const country = /^loc=([A-Z]{2})\r?$/m.exec(text)?.[1];
  return countries.has(country) ? country : null;
}

async function lookupCountry(fetcher, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // Served by this site's Cloudflare edge, so Vercel cannot mistake the
    // reverse proxy's address for the visitor. Do not retain/log the trace/IP.
    const response = await fetcher('/cdn-cgi/trace', {
      credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal,
    });
    return response.ok ? countryFromTrace(await response.text()) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function detectAnalyticsCountry(fetcher = fetch, { timeoutMs = 6000, attempts = 2 } = {}) {
  // A slow edge lookup must not turn a known US visitor into an unknown region.
  // Retry transient failures, but never infer permission from a failed lookup.
  for (let attempt = 0; attempt < attempts; attempt++) {
    const country = await lookupCountry(fetcher, timeoutMs);
    if (country) return country;
  }
  return null;
}

export function resolveAnalyticsConsent({ choice = 'unknown', country = null, privacySignal = false } = {}) {
  if (choice === 'denied' || choice === 'granted') return choice;
  if (privacySignal) return 'denied';
  if (!countries.has(country) || ANALYTICS_CONSENT_COUNTRIES.has(country)) return 'unknown';
  return 'granted';
}
